/*
 * Route Selectors (Render Builders)
 *
 * Purpose:
 * - Derive map-renderable route structures from canonical normalized state.
 * - Keep render-shape conversion in one place, independent from data source.
 *
 * Output shape:
 * - Compatible with existing RoutePoints/RouteLines expectations:
 *   { points: { routeId, routeType, geojson }, lines: { routeId, routeType, geojson } }
 */

import * as turf from "@turf/turf";
import { isActiveEntity } from "./RouteState.jsx";
import { courseBearing } from "@/utils/courseBearing.js";
import { vesselOptionToMeta } from "@/features/vessel/vesselMeta.js";

// Sample the great-circle arc at roughly this spacing. The vessel icon sits on
// the true arc (turf.destination), while the line is drawn as straight chords
// between these samples; mid-chord the chord deviates from the arc. At ~2 km
// spacing that deviation stays sub-pixel even at max zoom (~19), so the icon
// never visibly leaves the line. A fixed 256-point count was too coarse on long
// transoceanic legs (~21 km chords → ~16 m / ~70 px gap at zoom 19).
const GREATCIRCLE_CHORD_KM = 2;
const GREATCIRCLE_STEPS_MIN = 32;
const GREATCIRCLE_STEPS_MAX = 4096;

function greatCircleSteps(a, b) {
  let km = 0;
  try {
    km = turf.distance(a, b);
  } catch {
    return GREATCIRCLE_STEPS_MIN;
  }
  const n = Math.ceil(km / GREATCIRCLE_CHORD_KM) + 1;
  return Math.max(GREATCIRCLE_STEPS_MIN, Math.min(GREATCIRCLE_STEPS_MAX, n));
}

function buildLegCoordinates(wp1, wp2) {
  // wp1.legtype describes the leg from wp1 → wp2 (matches interpolateOnRoute).
  const legtype = wp1?.properties?.legtype;
  const a = wp1.geometry.coordinates;
  const b = wp2.geometry.coordinates;
  if (legtype !== "greatcircle") return [a, b];
  try {
    const gc = turf.greatCircle(turf.point(a), turf.point(b), {
      npoints: greatCircleSteps(a, b),
    });
    // MultiLineString when the great-circle crosses the antimeridian.
    // Concatenate parts; projectLinesToViewport unwraps longitudes across copies.
    if (gc.geometry.type === "MultiLineString") {
      return gc.geometry.coordinates.flat();
    }
    return gc.geometry.coordinates;
  } catch {
    return [a, b];
  }
}

export function toId(value) {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

export function isValidLonLat(lonlat) {
  return (
    Array.isArray(lonlat) &&
    lonlat.length === 2 &&
    Number.isFinite(Number(lonlat[0])) &&
    Number.isFinite(Number(lonlat[1]))
  );
}

// Astar responses use "2026-04-30 08:12:27" (space-separated, no zone);
// DB waypoints arrive already ISO. Coerce both to ISO-8601 UTC.
export function toIsoUtcTimestamp(value) {
  if (value == null) return null;
  if (typeof value === "number") {
    // Unix seconds → ISO. Multiply if it looks like seconds, leave if ms.
    const ms = value > 1e12 ? value : value * 1000;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 19) + "+00:00";
  }
  if (typeof value !== "string") return null;
  // "2026-04-30 08:12:27" → "2026-04-30T08:12:27+00:00"
  const normalised = value.includes("T")
    ? value
    : value.replace(" ", "T");
  const withZone =
    /Z|[+-]\d{2}:?\d{2}$/.test(normalised)
      ? normalised
      : normalised + "+00:00";
  return withZone;
}

function sortWaypointsForRender(waypoints = []) {
  return [...waypoints].sort((a, b) => {
    const aNum = Number(a?.number ?? 0);
    const bNum = Number(b?.number ?? 0);
    if (aNum !== bNum) return aNum - bNum;

    const aExt = Number(a?.extended_number ?? a?.extendedNumber ?? 0);
    const bExt = Number(b?.extended_number ?? b?.extendedNumber ?? 0);
    if (aExt !== bExt) return aExt - bExt;

    const aTs = new Date(a?.timestamp ?? 0).getTime();
    const bTs = new Date(b?.timestamp ?? 0).getTime();
    if (aTs !== bTs) return aTs - bTs;

    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
  });
}

export function selectRoutePointsGeojson(normalizedState, routeIdInput) {
  const routeId = toId(routeIdInput);
  if (routeId == null) return null;

  const route = normalizedState?.entities?.routesById?.[routeId] ?? null;
  const voyage =
    normalizedState?.entities?.voyagesById?.[toId(route?.voyageId)] ?? null;
  const vessel =
    normalizedState?.entities?.vesselsById?.[
      toId(route?.vesselId ?? voyage?.vesselId)
    ] ?? null;
  const routeType = route?.routeType ?? "planned";
  const waypoints = sortWaypointsForRender(
    normalizedState?.waypointsByRouteId?.[routeId] ?? [],
  ).filter((wp) => isValidLonLat(wp?.lonlat));

  const isReported = routeType === "reported";
  const bearingToNext = (idx) => {
    const wp = waypoints[idx];
    const next = waypoints[idx + 1];
    if (!next) return null;
    return courseBearing(
      [Number(wp.lonlat[0]), Number(wp.lonlat[1])],
      [Number(next.lonlat[0]), Number(next.lonlat[1])],
      wp?.legtype ?? "greatcircle",
    );
  };
  const courseOf = (wp, idx) =>
    isReported ? bearingToNext(idx) : (wp?.heading ?? bearingToNext(idx));

  const features = waypoints.map((wp, idx) => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [Number(wp.lonlat[0]), Number(wp.lonlat[1])],
    },
    properties: {
      id: wp?.id ?? null,
      routeId,
      number: Number.isFinite(Number(wp?.number)) ? Number(wp.number) : idx + 1,
      extended_number:
        Number.isFinite(Number(wp?.extended_number))
          ? Number(wp.extended_number)
          : Number.isFinite(Number(wp?.extendedNumber))
            ? Number(wp.extendedNumber)
            : 0,
      legType: wp?.legtype ?? "greatcircle",
      legtype: wp?.legtype ?? "greatcircle",
      basespeed: wp?.basespeed ?? null,
      timestamp: toIsoUtcTimestamp(wp?.timestamp),
      type: wp?.type ?? "original_point",
      vesselId: toId(route?.vesselId ?? voyage?.vesselId),
      voyageId: toId(route?.voyageId),
      vesselName: route?.vesselName ?? vessel?.name ?? null,
      routeName: route?.name ?? null,
      // Only reported `heading` is a sensor heading; the pathfinder's is a course.
      sog: wp?.sog ?? null,
      heading: isReported ? (wp?.heading ?? null) : null,
      course_bearing: courseOf(wp, idx),
      baseSpeedAdjustedKmh: wp?.baseSpeedAdjustedKmh ?? null,
      // Distance / time
      legDistanceKm: wp?.legDistanceKm ?? null,
      legTimeHour: wp?.legTimeHour ?? null,
      legSegmentDistanceKm: wp?.legSegmentDistanceKm ?? null,
      legSegmentTimeHour: wp?.legSegmentTimeHour ?? null,
      sumDistanceKm: wp?.sumDistanceKm ?? null,
      sumTimeHour: wp?.sumTimeHour ?? null,
      // Snapshot blobs
      weather_snapshot: wp?.weatherSnapshot ?? null,
      leg_data: wp?.legData ?? null,
      rnw_evaluation: wp?.rnwEvaluation ?? null,
      restrictions: wp?.restrictions ?? null,
      warnings: wp?.warnings ?? null,
      // Reported route data
      albis_reports: wp?.albisReports ?? null,
      albis_sensors: wp?.albisSensors ?? null,
    },
  }));

  return {
    type: "FeatureCollection",
    features,
  };
}

export function selectRouteLinesGeojson(normalizedState, routeIdInput) {
  const routeId = toId(routeIdInput);
  if (routeId == null) return null;

  const pointsGeojson = selectRoutePointsGeojson(normalizedState, routeId);
  const pointFeatures = pointsGeojson?.features ?? [];
  if (pointFeatures.length < 2) {
    return {
      type: "FeatureCollection",
      features: [],
    };
  }

  const features = [];
  for (let i = 0; i < pointFeatures.length - 1; i += 1) {
    const wp1 = pointFeatures[i];
    const wp2 = pointFeatures[i + 1];
    features.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: buildLegCoordinates(wp1, wp2),
      },
      properties: {
        analyse: {
          wp1,
          wp2,
        },
        type: "Feature",
      },
    });
  }

  return {
    type: "FeatureCollection",
    features,
  };
}

export function selectRenderableRoute(normalizedState, routeIdInput) {
  const routeId = toId(routeIdInput);
  if (routeId == null) return null;

  const route = normalizedState?.entities?.routesById?.[routeId] ?? null;
  const voyage =
    normalizedState?.entities?.voyagesById?.[toId(route?.voyageId)] ?? null;
  const vessel =
    normalizedState?.entities?.vesselsById?.[
      toId(route?.vesselId ?? voyage?.vesselId)
    ] ?? null;
  const routeType = route?.routeType ?? "planned";
  const state = route?.state ?? (routeType === "planned" ? "active" : null);

  const pointsGeojson = selectRoutePointsGeojson(normalizedState, routeId);
  const linesGeojson = selectRouteLinesGeojson(normalizedState, routeId);
  const isDraft = routeId < 0;

  return {
    jsonRequest: route?.jsonRequest ?? null,
    summary: route?.summary ?? null,
    optimisedFor: route?.optimisedFor ?? null,
    isDraft,
    sketch: route?.sketch === true,
    source: route?.source ?? null,
    draftContext: route?.draftContext ?? null,
    rawResponse: route?.rawResponse ?? null,
    points: {
      routeId,
      routeType,
      state,
      isDraft,
      vesselId: toId(route?.vesselId ?? voyage?.vesselId),
      voyageId: toId(route?.voyageId),
      vesselName: route?.vesselName ?? vessel?.name ?? null,
      name: route?.name ?? null,
      timestamp: route?.timestamp ?? null,
      geojson: pointsGeojson ?? { type: "FeatureCollection", features: [] },
    },
    lines: {
      routeId,
      routeType,
      state,
      isDraft,
      vesselId: toId(route?.vesselId ?? voyage?.vesselId),
      voyageId: toId(route?.voyageId),
      vesselName: route?.vesselName ?? vessel?.name ?? null,
      name: route?.name ?? null,
      timestamp: route?.timestamp ?? null,
      geojson: linesGeojson ?? { type: "FeatureCollection", features: [] },
    },
  };
}

export function selectRenderableRoutes(normalizedState, routeIds = []) {
  const ids = Array.from(
    new Set((routeIds ?? []).map((id) => toId(id)).filter((id) => id != null)),
  );
  return ids
    .map((routeId) => selectRenderableRoute(normalizedState, routeId))
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Draft selectors
// ---------------------------------------------------------------------------
//
// A discarded draft keeps its entity but loses its waypoints, so "has
// waypoints" is the liveness test.

const OBJECTIVE_DRAFT_NAMES = { time: "Time-optimised", fuel: "Fuel-optimised" };
const draftBaseName = (draft) =>
  OBJECTIVE_DRAFT_NAMES[draft?.optimisedFor] ?? "Manual route";
// displayName: label already derived against the full sibling set (map/table copies).
const givenDraftName = (draft) =>
  String(draft?.displayName ?? draft?.points?.name ?? draft?.name ?? "").trim();

// Single derivation of a draft's user-visible label (tree row, comparison
// tab/summary): the given name, else its objective, numbered when several
// unnamed siblings share it.
export function draftDisplayName(draft, siblings = []) {
  const given = givenDraftName(draft);
  if (given) return given;
  const base = draftBaseName(draft);
  const same = siblings.filter(
    (d) => !givenDraftName(d) && draftBaseName(d) === base,
  );
  if (same.length < 2) return base;
  const idx = same.findIndex(
    (d) => d?.points?.routeId === draft?.points?.routeId,
  );
  return idx < 0 ? base : `${base} ${idx + 1}`;
}

export function selectDraftRouteIds(normalizedState) {
  return Object.keys(normalizedState?.entities?.routesById ?? {})
    .map((id) => Number(id))
    .filter(
      (id) =>
        Number.isFinite(id) &&
        id < 0 &&
        (normalizedState?.waypointsByRouteId?.[id]?.length ?? 0) > 0,
    )
    .sort((a, b) => b - a);
}

export function selectDraftRoutes(normalizedState) {
  return selectRenderableRoutes(
    normalizedState,
    selectDraftRouteIds(normalizedState),
  );
}

export function selectDraftRouteIdForVessel(normalizedState, vesselId) {
  const targetVesselId = toId(vesselId);
  return (
    selectDraftRouteIds(normalizedState).find(
      (id) =>
        toId(normalizedState?.entities?.routesById?.[id]?.vesselId) ===
        targetVesselId,
    ) ?? null
  );
}

// ---------------------------------------------------------------------------
// Tree metadata selectors
// ---------------------------------------------------------------------------
//
// Relations are unordered unions — ordering lives here.

const byNameAsc = (a, b) =>
  String(a?.name ?? "").localeCompare(String(b?.name ?? ""));

const byTimestampDesc = (a, b) =>
  new Date(b?.timestamp ?? 0).getTime() - new Date(a?.timestamp ?? 0).getTime();

// Spec-view shape of one vessel's master data (null when unknown).
export function selectVesselMeta(normalizedState, vesselId) {
  const id = Number(vesselId);
  if (!Number.isFinite(id)) return null;
  const vessel = normalizedState?.entities?.vesselsById?.[id];
  return vessel ? vesselOptionToMeta(vessel) : null;
}

export function selectTreeVessels(normalizedState) {
  return (normalizedState?.relations?.vesselIds ?? [])
    .map((id) => normalizedState?.entities?.vesselsById?.[id])
    .filter(Boolean)
    .sort(byNameAsc);
}

export function filterVesselsByName(vessels, searchTerm) {
  const term = String(searchTerm ?? "").trim().toLowerCase();
  if (!term) return vessels;
  return vessels.filter((vessel) =>
    String(vessel?.name ?? "").toLowerCase().includes(term),
  );
}

export function selectFilteredVessels(normalizedState, searchTerm) {
  return filterVesselsByName(selectTreeVessels(normalizedState), searchTerm);
}

export function selectVoyagesForVessel(normalizedState, vesselId) {
  const id = toId(vesselId);
  return (normalizedState?.relations?.voyageIdsByVesselId?.[id] ?? [])
    .map((voyageId) => normalizedState?.entities?.voyagesById?.[voyageId])
    .filter(Boolean)
    .sort(byTimestampDesc);
}

export function selectRoutesForVoyage(normalizedState, voyageId) {
  const id = toId(voyageId);
  return (normalizedState?.relations?.routeIdsByVoyageId?.[id] ?? [])
    .filter((routeId) => toId(routeId) > 0) // drafts render via draftRoutes
    .map((routeId) => normalizedState?.entities?.routesById?.[routeId])
    .filter(Boolean)
    .sort(byTimestampDesc);
}

// Active route first, else the newest suggested route.
export function selectActiveOrSuggestedRoute(routes) {
  const list = [...(Array.isArray(routes) ? routes : [])].sort(byTimestampDesc);
  return (
    list.find(isActiveEntity) ??
    list.find((r) => String(r?.state ?? "").toLowerCase() === "suggested") ??
    null
  );
}

export function selectNextDraftRouteId(normalizedState) {
  const usedIds = Object.keys(normalizedState?.entities?.routesById ?? {})
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id < 0);
  return Math.min(0, ...usedIds) - 1;
}
